import React, { useState } from 'react';
import {
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineClipboard,
  HiOutlineCheck,
  HiOutlineLogin,
} from 'react-icons/hi';

const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (e) {
      // clipboard might be blocked; fall back to selection
      const ta = document.createElement('textarea');
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy ${label}`}
      aria-label={`Copy ${label}`}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-primary-600 transition-colors hover:bg-primary-50"
    >
      {copied ? (
        <HiOutlineCheck className="h-4 w-4 text-emerald-600" />
      ) : (
        <HiOutlineClipboard className="h-4 w-4" />
      )}
    </button>
  );
};

const Field = ({ label, value, masked = false, mono = true }) => {
  const [reveal, setReveal] = useState(!masked);
  const shown = !masked || reveal;

  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral sm:w-20 sm:shrink-0 sm:text-[11px]">
        {label}
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-1 rounded-lg bg-primary-50/70 px-3 py-2 ring-1 ring-primary-100">
        <span
          className={`min-w-0 flex-1 truncate text-sm text-neutral-dark ${mono ? 'font-mono' : ''}`}
        >
          {shown ? value : '•'.repeat(Math.min(value.length, 14))}
        </span>
        {masked && (
          <button
            type="button"
            onClick={() => setReveal((v) => !v)}
            title={reveal ? 'Hide' : 'Reveal'}
            aria-label={reveal ? 'Hide password' : 'Reveal password'}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-primary-600 transition-colors hover:bg-primary-100"
          >
            {reveal ? (
              <HiOutlineEyeOff className="h-4 w-4" />
            ) : (
              <HiOutlineEye className="h-4 w-4" />
            )}
          </button>
        )}
        <CopyButton value={value} label={label} />
      </div>
    </div>
  );
};

const CredentialRow = ({ cred }) => {
  const { role, email, password, loginUrl, notes } = cred;

  const quickSignIn = async () => {
    if (!loginUrl) return;
    try {
      // Copy "email\tpassword" so a single paste fills both fields on most login forms,
      // and also stage just the password if email is autofilled.
      await navigator.clipboard.writeText(`${email}\t${password}`);
    } catch (e) {
      /* clipboard blocked — silently continue */
    }
    window.open(loginUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-3 rounded-xl bg-white p-3 ring-1 ring-primary-100 sm:p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="h-2 w-2 shrink-0 rounded-full bg-primary-500" />
          <h5 className="truncate text-sm font-semibold text-neutral-dark">{role}</h5>
        </div>
        {loginUrl && (
          <button
            type="button"
            onClick={quickSignIn}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white shadow-card transition-colors hover:bg-primary-600"
            title="Copies credentials to clipboard and opens the login page"
          >
            <HiOutlineLogin className="h-3.5 w-3.5" />
            Quick Sign In
          </button>
        )}
      </div>

      <div className="space-y-2">
        <Field label="Email" value={email} />
        <Field label="Password" value={password} masked />
      </div>

      {notes && (
        <p className="border-l-2 border-primary-200 pl-2 text-[11px] italic text-neutral">
          {notes}
        </p>
      )}
    </div>
  );
};

export default CredentialRow;
